import React, { useMemo, useState, useEffect } from "react";

// Base table component
import { DataTable, TableColumnType } from "components/organisms/DataTable";
import MDBox from "components/atoms/MDBox/MDBox";
import MDTypography from "components/atoms/MDTypography/MDTypography";
import PaginationControls from "components/molecules/PaginationControls/PaginationControls";
import MDInput from "components/atoms/MDInput/MDInput";
import MDButton from "components/atoms/MDButton/MDButton";
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { MUIColors } from "models/StyleModels";
import colors from "assets/theme/base/colors";
import ActionSlugs from 'services/PermissionsService';

interface PatientDataTableProps {
  entriesPerPage?: {
    defaultValue: number;
    entries: number[];
  };
  showTotalEntries?: boolean;
  table: {
    columns: TableColumnType[];
    rows: { [key: string]: React.ReactNode }[];
  };
  pagination?: {
    variant: 'contained' | 'gradient';
    color: string;
  };
  isSorted?: boolean[];
  noEndBorder?: boolean;
  dataLoading?: boolean;
  dataError?: boolean;
  handleRetry?(): void;
  sx?: { [key: string]: any };
  handleRowClick?(args: any): any;
  emptyMessage?: string;
  user?: any;
  onAddPatient?: () => void;

  // Optional pagination props
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  totalCount?: number | null;
  totalReturned?: number;
  pageSize?: number;

  // Search props
  onSearchChange?: (query: string) => void;
  useServerSideSearch?: boolean;
}

const PatientDataTable: React.FC<PatientDataTableProps> = ({
  entriesPerPage,
  showTotalEntries,
  table,
  isSorted,
  noEndBorder,
  dataLoading,
  dataError,
  handleRetry,
  handleRowClick,
  emptyMessage,
  user,
  onAddPatient,
  isFetchingNextPage = false,
  hasNextPage = false,
  onLoadMore,
  totalCount,
  totalReturned,
  pageSize = 15,
  onSearchChange,
  useServerSideSearch = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  // Pagination state (only used when onLoadMore is provided)
  const [currentDisplayPage, setCurrentDisplayPage] = useState(0);
  const [pendingPage, setPendingPage] = useState<number | null>(null);
  const [pendingPageRowCount, setPendingPageRowCount] = useState<number | null>(null);
  const displayPageSize = 15;

  // This manages state so that the pagination is subtle and smooth.
  useEffect(() => {
    if (onLoadMore && pendingPage !== null && !isFetchingNextPage) {
      // Only apply pending page if we have more data than when we started the request
      const hasNewData = pendingPageRowCount !== null && table.rows.length > pendingPageRowCount;

      if (hasNewData) {
        setCurrentDisplayPage(pendingPage);
        setPendingPage(null);
        setPendingPageRowCount(null);
      } else {
        // Waiting for new data to arrive
      }
    }
  }, [onLoadMore, pendingPage, isFetchingNextPage, currentDisplayPage, table.rows.length, pendingPageRowCount]);

  // Keeps users from getting stuck on a page that doesn't exist.
  useEffect(() => {
    if (onLoadMore && totalCount && currentDisplayPage > 0) {
      const totalPages = Math.ceil(totalCount / displayPageSize);
      if (currentDisplayPage >= totalPages) {
        setCurrentDisplayPage(Math.max(0, totalPages - 1));
      }
    }
  }, [onLoadMore, totalCount, currentDisplayPage]);

  // Calculate pagination based on total count from server
  const paginationInfo = useMemo(() => {
    if (!onLoadMore) {
      return null;
    }


    const totalPages = totalCount ? Math.ceil(totalCount / displayPageSize) : Math.ceil(table.rows.length / displayPageSize);
    const canPreviousPage = currentDisplayPage > 0;
    const canNextPage = currentDisplayPage < totalPages - 1;

    const loadedPages = Math.ceil(table.rows.length / displayPageSize);
    const needsMoreData = currentDisplayPage >= loadedPages - 1 && hasNextPage;

    return {
      totalPages,
      currentPage: currentDisplayPage,
      canPreviousPage,
      canNextPage,
      loadedPages,
      needsMoreData,
      entriesStart: (currentDisplayPage * displayPageSize) + 1,
      entriesEnd: Math.min((currentDisplayPage + 1) * displayPageSize, totalCount || table.rows.length),
      totalCount: totalCount || table.rows.length,
    };
  }, [onLoadMore, totalCount, table.rows.length, currentDisplayPage, hasNextPage]);

  const handleDisplayPageChange = (page: number) => {
    if (!onLoadMore) return;

    const loadedPages = Math.ceil(table.rows.length / displayPageSize);

    // If user is trying to go to a page we don't have data for yet, load more
    if (page >= loadedPages - 1 && hasNextPage && onLoadMore) {

      setPendingPage(page);
      setPendingPageRowCount(table.rows.length);
      onLoadMore();
    } else {
      setCurrentDisplayPage(page);
    }
  };

  // Slice the data for current display page when pagination is enabled
  const paginatedTableData = useMemo(() => {
    if (!onLoadMore) {
      return table;
    }

    const startIndex = currentDisplayPage * displayPageSize;
    const endIndex = startIndex + displayPageSize;
    const paginatedRows = table.rows.slice(startIndex, endIndex);

    return {
      ...table,
      rows: paginatedRows
    };
  }, [table, onLoadMore, currentDisplayPage]);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (paginationInfo?.canPreviousPage) {
      handleDisplayPageChange(currentDisplayPage - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationInfo?.canNextPage) {
      handleDisplayPageChange(currentDisplayPage + 1);
    }
  };

  // Custom sort logic for patient list - only allow sorting on specific columns
  const customSortLogic = (column: TableColumnType) => {
    return column.Header === 'Patient Name' || column.Header === 'Appointment Date';
  };

  // Custom sort value logic for patient list
  const getSortedValue = (column: TableColumnType) => {
    if (column.Header !== 'Patient Name' && column.Header !== 'Appointment Date') {
      return false;
    }
    return 'none'; // This will be overridden by the base component's internal logic
  };

  // Client-side search filtering (only when not using server-side search)
  const searchedTable = useMemo(() => {
    if (useServerSideSearch || !searchQuery) return table;

    const filteredRows = table.rows.filter((row) =>
      Object.values(row).some((value) =>
        typeof value === 'string' && value.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );

    return { ...table, rows: filteredRows };
  }, [table, searchQuery, useServerSideSearch]);

  const tableData = onLoadMore ? paginatedTableData : searchedTable;

  return (
    <>
      <MDBox pb={2} display="flex" justifyContent="center" alignItems="center" gap={1}>
        <MDInput
          placeholder="Patient Search"
          value={searchQuery}
          size="small"
          fullWidth
          sx={{ width: 'auto' }}
          onChange={({ currentTarget }) => {
            const value = currentTarget.value;
            setSearchQuery(value);
            if (onSearchChange) {
              onSearchChange(value);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="medium" />
              </InputAdornment>
            ),
          }}
        />

        {user?.can(ActionSlugs.CREATE$PATIENT) && onAddPatient && (
          <MDButton
            variant="contained"
            color={MUIColors.SECONDARY}
            onClick={onAddPatient}
          >
            <FontAwesomeIcon color={colors.white.focus} icon={faPlus} style={{ marginRight: '10px' }} />
            New Patient
          </MDButton>
        )}
      </MDBox>

      <DataTable
        entriesPerPage={entriesPerPage}
        showTotalEntries={onLoadMore ? false : showTotalEntries} // We handle this in PaginationControls when pagination is enabled
        table={tableData}
        isSorted={isSorted}
        noEndBorder={noEndBorder}
        dataLoading={dataLoading}
        dataError={dataError}
        handleRetry={handleRetry}
        handleRowClick={handleRowClick}
        emptyMessage={emptyMessage}
        customSortLogic={customSortLogic}
        getSortedValue={getSortedValue}
        tableContainerSx={{ boxShadow: "none" }}
        getRowKey={(row) => {
          return `patient-${row.json.patient_id}`
        }}
      />

      {onLoadMore && paginationInfo && (
        <PaginationControls
          currentPage={paginationInfo.currentPage}
          totalPages={paginationInfo.totalPages}
          totalCount={paginationInfo.totalCount}
          pageSize={displayPageSize}
          onPageChange={handleDisplayPageChange}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          showTotalEntries={true}
          isLoading={isFetchingNextPage}
        />
      )}

      {onLoadMore && isFetchingNextPage && (
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={1}
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 1,
            mt: 1
          }}
        >
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
            Loading more patients...
          </MDTypography>
        </MDBox>
      )}
    </>
  );
};

export default PatientDataTable;